import React, { useEffect, useState, Component } from 'react';
import { useOrders } from '../contexts/OrderContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { storage } from '../utils/storage';
import { User, TourType, SuggestedTour } from '../types';
import { ZONES } from '../constants/zones';
import { Truck, Plus, X, AlertCircle, Sparkles, CheckCircle } from 'lucide-react';
export function TourManagementPage() {
  const {
    orders,
    tours,
    createTour,
    refreshData,
    getSuggestedTours,
    approveSuggestedTour
  } = useOrders();
  const [isCreating, setIsCreating] = useState(false);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [suggestedTours, setSuggestedTours] = useState<SuggestedTour[]>([]);
  // Form State
  const [formData, setFormData] = useState({
    type: 'COLLECTE' as TourType,
    zone: '',
    driverId: '',
    vehicle: '',
    date: new Date().toISOString().split('T')[0],
    selectedOrders: [] as string[]
  });
  useEffect(() => {
    const allUsers = storage.getUsers();
    setDrivers(allUsers.filter(u => u.role === 'CHAUFFEUR' && u.active));
    // Load suggested tours
    const suggestions = getSuggestedTours();
    setSuggestedTours(suggestions);
  }, [orders]);
  // Filter available orders for the tour
  const availableOrders = orders.filter(o => {
    // Must match zone
    if (formData.zone && o.zone !== formData.zone) return false;
    // Must match status based on tour type
    if (formData.type === 'COLLECTE') {
      return o.status === 'CREEE';
    } else {
      return o.status === 'STOCKEE_LIVRAISON' || o.status === 'RETOUR_DEPOT';
    }
  }).sort((a, b) => b.priority - a.priority); // Sort by priority
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.selectedOrders.length === 0) {
      alert('Veuillez sélectionner au moins une commande');
      return;
    }
    try {
      await createTour({
        type: formData.type,
        zone: formData.zone,
        driverId: formData.driverId,
        vehicle: formData.vehicle,
        date: formData.date,
        orderIds: formData.selectedOrders
      });
      setIsCreating(false);
      setFormData({
        ...formData,
        selectedOrders: [],
        zone: '',
        driverId: '',
        vehicle: ''
      });
      alert('Tournée créée avec succès');
      // Refresh suggestions
      setSuggestedTours(getSuggestedTours());
    } catch (error) {
      console.error(error);
      alert('Erreur lors de la création');
    }
  };
  const handleApproveSuggestion = async (suggestion: SuggestedTour, driverId: string, vehicle: string) => {
    if (!driverId || !vehicle) {
      alert('Veuillez sélectionner un chauffeur et un véhicule');
      return;
    }
    try {
      await approveSuggestedTour(suggestion, driverId, vehicle);
      alert('Tournée approuvée et créée avec succès');
      setSuggestedTours(getSuggestedTours());
    } catch (error) {
      console.error(error);
      alert("Erreur lors de l'approbation");
    }
  };
  const toggleOrderSelection = (orderId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedOrders: prev.selectedOrders.includes(orderId) ? prev.selectedOrders.filter(id => id !== orderId) : [...prev.selectedOrders, orderId]
    }));
  };
  const handleCancelTour = (tour: any) => {
    if (window.confirm('Voulez-vous vraiment annuler/terminer cette tournée ?')) {
      const updatedTour = {
        ...tour,
        status: 'TERMINEE'
      };
      storage.saveTour(updatedTour);
      refreshData();
    }
  };
  return <div className="space-y-6" data-id="element-647">
      <div className="flex justify-between items-center" data-id="element-648">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-649">
          Gestion des Tournées
        </h1>
        {!isCreating && <Button onClick={() => setIsCreating(true)} leftIcon={<Plus className="w-4 h-4" data-id="element-651" />} data-id="element-650">
            Nouvelle Tournée Manuelle
          </Button>}
      </div>

      {/* Suggested Tours Section */}
      {suggestedTours.length > 0 && !isCreating && <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6" data-id="element-652">
          <div className="flex items-center mb-4" data-id="element-653">
            <Sparkles className="w-6 h-6 text-blue-600 mr-2" data-id="element-654" />
            <h2 className="text-xl font-bold text-gray-900" data-id="element-655">
              Tournées Suggérées pour Demain
            </h2>
          </div>
          <p className="text-sm text-gray-600 mb-6" data-id="element-656">
            Tournées auto-générées basées sur les commandes prêtes. Les retours
            sont prioritaires.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-657">
            {suggestedTours.map((suggestion, idx) => <SuggestedTourCard key={idx} suggestion={suggestion} drivers={drivers} onApprove={handleApproveSuggestion} orders={orders} data-id="element-658" />)}
          </div>
        </div>}

      {isCreating ? <Card title="Créer une nouvelle tournée" data-id="element-659">
          <form onSubmit={handleCreate} className="space-y-6" data-id="element-660">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-661">
              <div data-id="element-662">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-663">
                  Type de tournée
                </label>
                <div className="flex space-x-4" data-id="element-664">
                  <label className="flex items-center space-x-2 border rounded-lg p-3 flex-1 cursor-pointer hover:bg-gray-50" data-id="element-665">
                    <input type="radio" name="type" value="COLLECTE" checked={formData.type === 'COLLECTE'} onChange={e => setFormData({
                  ...formData,
                  type: 'COLLECTE' as TourType,
                  selectedOrders: []
                })} className="text-blue-600" data-id="element-666" />
                    <span data-id="element-667">Collecte</span>
                  </label>
                  <label className="flex items-center space-x-2 border rounded-lg p-3 flex-1 cursor-pointer hover:bg-gray-50" data-id="element-668">
                    <input type="radio" name="type" value="LIVRAISON" checked={formData.type === 'LIVRAISON'} onChange={e => setFormData({
                  ...formData,
                  type: 'LIVRAISON' as TourType,
                  selectedOrders: []
                })} className="text-green-600" data-id="element-669" />
                    <span data-id="element-670">Livraison</span>
                  </label>
                </div>
              </div>

              <Select label="Zone" value={formData.zone} onChange={e => setFormData({
            ...formData,
            zone: e.target.value,
            selectedOrders: []
          })} options={ZONES.map(z => ({
            value: z,
            label: z
          }))} required data-id="element-671" />

              <Select label="Chauffeur" value={formData.driverId} onChange={e => setFormData({
            ...formData,
            driverId: e.target.value
          })} options={drivers.map(d => ({
            value: d.id,
            label: d.name
          }))} required data-id="element-672" />

              <Input label="Véhicule" value={formData.vehicle} onChange={e => setFormData({
            ...formData,
            vehicle: e.target.value
          })} placeholder="ex: Partner 123 TN 4567" required data-id="element-673" />

              <Input label="Date" type="date" value={formData.date} onChange={e => setFormData({
            ...formData,
            date: e.target.value
          })} required data-id="element-674" />
            </div>

            {/* Order Selection */}
            {formData.zone && <div className="border-t border-gray-200 pt-4" data-id="element-675">
                <h3 className="font-medium text-gray-900 mb-3" data-id="element-676">
                  Sélectionner les commandes ({availableOrders.length}{' '}
                  disponibles)
                </h3>
                {availableOrders.length === 0 ? <p className="text-gray-500 text-sm italic" data-id="element-677">
                    Aucune commande disponible pour cette zone et ce type.
                  </p> : <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200" data-id="element-678">
                    {availableOrders.map(order => <label key={order.id} className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${order.status === 'RETOUR_DEPOT' ? 'bg-red-50' : ''}`} data-id="element-679">
                        <input type="checkbox" checked={formData.selectedOrders.includes(order.id)} onChange={() => toggleOrderSelection(order.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-680" />
                        <div className="ml-3 flex-1" data-id="element-681">
                          <div className="flex justify-between items-center" data-id="element-682">
                            <div data-id="element-683">
                              <span className="font-medium text-gray-900" data-id="element-684">
                                {order.clientName}
                              </span>
                              {order.status === 'RETOUR_DEPOT' && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold" data-id="element-685">
                                  RETOUR - Priorité {order.priority}
                                </span>}
                              {order.deliveryAttempts > 0 && <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded" data-id="element-686">
                                  Tentative #{order.deliveryAttempts + 1}
                                </span>}
                            </div>
                            <span className="text-sm text-gray-500" data-id="element-687">
                              {order.quantity} {order.type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500" data-id="element-688">
                            {order.address}
                          </p>
                        </div>
                      </label>)}
                  </div>}
                <p className="text-sm text-gray-500 mt-2" data-id="element-689">
                  {formData.selectedOrders.length} commandes sélectionnées
                </p>
              </div>}

            <div className="flex justify-end gap-3 pt-4" data-id="element-690">
              <Button type="button" variant="secondary" onClick={() => setIsCreating(false)} data-id="element-691">
                Annuler
              </Button>
              <Button type="submit" data-id="element-692">Créer la Tournée</Button>
            </div>
          </form>
        </Card> : <Card title="Tournées Existantes" data-id="element-693">
          <div className="overflow-x-auto" data-id="element-694">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-695">
              <thead className="bg-gray-50" data-id="element-696">
                <tr data-id="element-697">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-698">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-699">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-700">
                    Zone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-701">
                    Chauffeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-702">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-703">
                    Commandes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-704">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-705">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-706">
                {tours.map(tour => {
              const driver = drivers.find(d => d.id === tour.driverId);
              return <tr key={tour.id} data-id="element-707">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-708">
                        {tour.id}
                        {tour.isAutoGenerated && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded" data-id="element-709">
                            AUTO
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-710">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${tour.type === 'LIVRAISON' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-711">
                          {tour.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-712">
                        {tour.zone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-713">
                        {driver?.name || tour.driverId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-714">
                        {new Date(tour.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-715">
                        {tour.orderIds.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-716">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${tour.status === 'EN_COURS' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-717">
                          {tour.status === 'EN_COURS' ? 'En Cours' : 'Terminée'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-718">
                        {tour.status === 'EN_COURS' && <button onClick={() => handleCancelTour(tour)} className="text-red-600 hover:text-red-900" data-id="element-719">
                            Terminer/Annuler
                          </button>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </Card>}
    </div>;
}
// Suggested Tour Card Component
function SuggestedTourCard({
  suggestion,
  drivers,
  onApprove,
  orders
}: {
  suggestion: SuggestedTour;
  drivers: User[];
  onApprove: (suggestion: SuggestedTour, driverId: string, vehicle: string) => void;
  orders: any[];
}) {
  const [selectedDriver, setSelectedDriver] = useState('');
  const [vehicle, setVehicle] = useState('');
  const tourOrders = orders.filter(o => suggestion.orderIds.includes(o.id));
  const returnOrders = tourOrders.filter(o => o.status === 'RETOUR_DEPOT');
  return <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors" data-id="element-720">
      <div className="space-y-4" data-id="element-721">
        <div data-id="element-722">
          <h3 className="font-bold text-lg text-gray-900 flex items-center" data-id="element-723">
            <Truck className="w-5 h-5 mr-2 text-blue-600" data-id="element-724" />
            {suggestion.zone}
          </h3>
          <p className="text-sm text-gray-500" data-id="element-725">
            {new Date(suggestion.date).toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}
          </p>
        </div>

        <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-726">
          <div className="flex justify-between items-center mb-2" data-id="element-727">
            <span className="text-sm font-medium text-gray-700" data-id="element-728">Commandes</span>
            <span className="text-2xl font-bold text-blue-600" data-id="element-729">
              {suggestion.totalOrders}
            </span>
          </div>
          {suggestion.priorityCount > 0 && <div className="flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-730">
              <AlertCircle className="w-3 h-3 mr-1" data-id="element-731" />
              {suggestion.priorityCount} retour(s) prioritaire(s)
            </div>}
        </div>

        <div className="space-y-2" data-id="element-732">
          <Select label="Chauffeur" value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)} options={drivers.map(d => ({
          value: d.id,
          label: d.name
        }))} data-id="element-733" />
          <Input label="Véhicule" value={vehicle} onChange={e => setVehicle(e.target.value)} placeholder="ex: Partner 123" data-id="element-734" />
        </div>

        <Button fullWidth onClick={() => onApprove(suggestion, selectedDriver, vehicle)} disabled={!selectedDriver || !vehicle} leftIcon={<CheckCircle className="w-4 h-4" data-id="element-736" />} className="bg-green-600 hover:bg-green-700" data-id="element-735">
          Approuver & Créer
        </Button>
      </div>
    </Card>;
}